
import React from 'react';

export const TaxImplicationsTerms: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Tax Implications and Capital Gains Strategies
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Understanding tax implications is crucial for maximizing your after-tax proceeds from apartment building sales. 
            Strategic planning can save you thousands in taxes.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Capital Gains Tax Basics</h3>
              
              <div className="space-y-6">
                <div className="bg-red-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-red-800 mb-3">Short-Term Capital Gains</h4>
                  <p className="text-gray-700 mb-3">
                    Properties held for less than one year are taxed as ordinary income at rates up to 37%. 
                    This significantly reduces your net proceeds.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-red-200">
                    <div className="text-sm">
                      <div className="flex justify-between mb-1">
                        <span>Tax Rate:</span>
                        <span className="font-bold text-red-600">10% - 37%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Holding Period:</span>
                        <span className="font-medium">&lt; 1 year</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-800 mb-3">Long-Term Capital Gains</h4>
                  <p className="text-gray-700 mb-3">
                    Properties held for more than one year qualify for preferential tax rates, significantly 
                    reducing your tax burden and increasing net proceeds.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-green-200">
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>0% Rate (Low Income):</span>
                        <span className="font-bold text-green-600">$0 - $44,625</span>
                      </div>
                      <div className="flex justify-between">
                        <span>15% Rate (Middle Income):</span>
                        <span className="font-bold text-green-600">$44,626 - $492,300</span>
                      </div>
                      <div className="flex justify-between">
                        <span>20% Rate (High Income):</span>
                        <span className="font-bold text-green-600">$492,301+</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-yellow-800 mb-3">Depreciation Recapture</h4>
                  <p className="text-gray-700 mb-3">
                    Previously claimed depreciation is "recaptured" and taxed at 25% for individuals. 
                    This applies to the depreciation taken during ownership.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-yellow-200">
                    <div className="text-sm">
                      <div className="flex justify-between">
                        <span>Tax Rate:</span>
                        <span className="font-bold text-yellow-600">25%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/938f76e9aaae6c0d3a38cade346cdf1c.jpg"
              alt="Capital gains tax planning and calculation for apartment buildings"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Tax Planning Strategies</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-refresh-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">1031 Exchange</h4>
              <p className="text-gray-600 text-sm mb-3">
                Defer capital gains taxes by reinvesting proceeds into like-kind property. Must identify replacement 
                property within 45 days and close within 180 days.
              </p>
              <div className="text-xs text-blue-600 font-medium">
                Tax Deferral: 100% of capital gains
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-schedule-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Installment Sales</h4>
              <p className="text-gray-600 text-sm mb-3">
                Spread capital gains over multiple years by accepting payments over time. Helps manage tax brackets 
                and reduces overall tax burden.
              </p>
              <div className="text-xs text-green-600 font-medium">
                Benefit: Lower effective tax rate
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-trophy-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Opportunity Zones</h4>
              <p className="text-gray-600 text-sm mb-3">
                Invest capital gains in qualified opportunity zone funds to defer and potentially reduce capital gains taxes. 
                Requires 10-year holding period for maximum benefit.
              </p>
              <div className="text-xs text-purple-600 font-medium">
                Tax Reduction: Up to 15% step-up in basis
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-heart-pulse-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Charitable Remainder Trust</h4>
              <p className="text-gray-600 text-sm mb-3">
                Donate property to a charitable trust, receive income for life, and get immediate tax deduction. 
                Good for highly appreciated properties.
              </p>
              <div className="text-xs text-red-600 font-medium">
                Benefits: Income + tax deduction + charity
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calculator-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Cost Segregation</h4>
              <p className="text-gray-600 text-sm mb-3">
                Accelerate depreciation deductions before sale to offset other income and reduce overall tax burden. 
                Best implemented early in ownership.
              </p>
              <div className="text-xs text-yellow-600 font-medium">
                Strategy: Maximize pre-sale deductions
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Timing Optimization</h4>
              <p className="text-gray-600 text-sm mb-3">
                Time the sale to optimize tax brackets, offset gains with losses, or align with other tax planning strategies. 
                Year-end planning is crucial.
              </p>
              <div className="text-xs text-teal-600 font-medium">
                Focus: Tax bracket management
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=1031%20exchange%20process%20diagram%20with%20timeline%20and%20requirements%2C%20like-kind%20property%20exchange%20for%20apartment%20buildings%2C%20qualified%20intermediary%20facilitating%20real%20estate%20exchange%2C%20tax-deferred%20investment%20strategy%20visualization&width=600&height=400&seq=1031-exchange-process&orientation=landscape"
              alt="1031 exchange process and timeline for apartment buildings"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">1031 Exchange Deep Dive</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Requirements</h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm">Like-kind property (investment to investment)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm">Equal or greater value replacement property</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm">Use qualified intermediary</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm">Follow strict timing rules</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-800 mb-4">Critical Timeline</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Close on relinquished property</span>
                      <span className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">Day 0</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Identify replacement property</span>
                      <span className="bg-yellow-200 text-yellow-800 px-3 py-1 rounded-full text-xs font-medium">Day 45</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Close on replacement property</span>
                      <span className="bg-green-200 text-green-800 px-3 py-1 rounded-full text-xs font-medium">Day 180</span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-green-800 mb-2">Potential Tax Savings</h5>
                  <p className="text-green-700 text-sm">
                    On a $1.5M sale with $500K in gains, a 1031 exchange could defer $75K-125K in taxes, 
                    preserving capital for reinvestment and compound growth.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-8 rounded-2xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Tax Planning Is Wealth Preservation</h3>
            <p className="text-lg opacity-90 leading-relaxed mb-4">
              Strategic tax planning can preserve 20-40% more of your sale proceeds compared to no planning. 
              The complexity of tax laws makes professional guidance essential for maximizing your after-tax wealth.
            </p>
            <div className="text-sm opacity-75">
              <strong>Important:</strong> Tax laws change frequently. Always consult with qualified tax professionals 
              for current regulations and personalized advice.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
